import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';
import {
  mediaQueryLarge,
  requestIdleCallback,
  startViewTransition,
  showLoader,
  hideLoader,
} from '@theme/utilities';

/** The next chunk is requested when the end of the loaded cards is within this distance below the viewport. */
const NEXT_CHUNK_MARGIN = '200px';
/** Placeholder (skeleton) cards appear only when the end is this close AND the next chunk has not arrived yet. */
const SKELETON_MARGIN = '100px';
/** Safety net for visitors: if nobody scrolls, the remaining chunks load after this delay. */
const SAFETY_TIMER_MS = 20000;
/** Crawlers cannot scroll: for them the remaining chunks load this long after the page is ready. */
const CRAWLER_DELAY_MS = 1000;
const CRAWLER_USER_AGENT = /bot|crawl|spider|slurp|bingpreview|lighthouse|pagespeed|headless/i;

/**
 * SHPLC-7654: phased collection list.
 *
 * Used only by `sections/main-collection-phased.liquid` (template `collection.phased`). The server renders ONE chunk
 * (`paginate by <chunk size>`, default 16) so a heavy collection page stays far below Shopify's render time limit.
 * This component then loads the remaining chunks of the same logical page (default 3 chunks = 48 products) through the
 * Section Rendering API and inserts the cards in order.
 *
 * Page numbers: the UI and the browser URL use LOGICAL pages (48 products). Shopify pages are chunks, so
 * logical page L = Shopify pages (L - 1) * subpages + 1 ... L * subpages.
 *
 * It registers the same `<results-list>` tag as `results-list.js` because facets, grid density controls and
 * pagination handlers look the element up by that tag. Never load both scripts on one page.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid]
 * @extends Component<Refs>
 */
export default class ResultsListPhased extends Component {
  /** Incremented whenever a running load must be abandoned (filter change, new morph, disconnect). */
  #runId = 0;

  /** @type {HTMLElement | null} */
  #skeletonTrail = null;

  /** Shopify pages (chunks) of the block on screen that are not loaded yet, in page order. */
  #queue = [];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {IntersectionObserver | null} */
  #skeletonObserver = null;

  #safetyTimer = 0;
  #chunkLoading = false;
  #failed = false;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    document.addEventListener(ThemeEvents.sectionMorphed, this.#handleSectionMorphed);

    this.setAttribute('initialized', '');
    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#runId++;
    this.#stopLoading();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    document.removeEventListener(ThemeEvents.sectionMorphed, this.#handleSectionMorphed);
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  /* ------------------------------------------------------------------ */
  /* Phased loading                                                      */
  /* ------------------------------------------------------------------ */

  #handleFilterUpdate = () => {
    // Filters/sort re-render the section; whatever is loading now belongs to the old result set.
    this.#runId++;
    this.#stopLoading();
    this.#removeSkeleton();
  };

  /** @param {Event} event */
  #handleSectionMorphed = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (detail?.sectionId && detail.sectionId !== this.sectionId) return;
    this.#start();
  };

  /**
   * @returns {{ grid: HTMLUListElement, subpages: number, chunkSize: number, lastPage: number, currentPage: number, total: number } | null}
   */
  #readConfig() {
    const grid = this.refs.grid;
    if (!(grid instanceof HTMLUListElement) || grid.dataset.phased !== 'true') return null;

    const subpages = Number(grid.dataset.phasedSubpages);
    const chunkSize = Number(grid.dataset.phasedChunkSize);
    const lastPage = Number(grid.dataset.lastPage);
    const currentPage = Number(grid.dataset.phasedCurrentPage);
    const total = Number(grid.dataset.phasedTotalResults);

    if (![subpages, chunkSize, lastPage, currentPage].every((n) => Number.isFinite(n) && n >= 1)) return null;

    return { grid, subpages, chunkSize, lastPage, currentPage, total: Number.isFinite(total) ? total : 0 };
  }

  /** First Shopify page of the logical page the browser URL asks for. */
  #startPageFromUrl(subpages, lastPage) {
    const requested = Number(new URL(window.location.href).searchParams.get('page'));
    const logicalTotal = Math.max(1, Math.ceil(lastPage / subpages));

    let logical = 1;
    if (Number.isFinite(requested) && requested >= 1) {
      if (requested <= logicalTotal) logical = Math.floor(requested);
      else if (requested <= lastPage) logical = Math.floor((requested - 1) / subpages) + 1;
      else logical = logicalTotal;
    }
    return (logical - 1) * subpages + 1;
  }

  /** Loads every missing chunk of the logical page that is on screen. */
  async #start() {
    const config = this.#readConfig();
    this.#stopLoading();
    this.#removeSkeleton();
    if (!config) return;

    const runId = ++this.#runId;
    const { subpages, lastPage, currentPage } = config;

    // Direct visit of a logical page > 1 (or a shared URL): the server rendered Shopify page `page` = a chunk of the
    // wrong block. Re-render the section from the first chunk of the requested logical page; the morph restarts us.
    const startPage = this.#startPageFromUrl(subpages, lastPage);
    if (currentPage !== startPage && Math.floor((currentPage - 1) / subpages) !== Math.floor((startPage - 1) / subpages)) {
      try {
        await sectionRenderer.renderSection(this.sectionId, { cache: false, url: this.#pageUrl(startPage) });
        return;
      } catch (error) {
        // Fall through: at least complete the block that is already on screen.
        console.error('[phased] could not render the requested page', error);
      }
    }

    const blockStart = Math.floor((currentPage - 1) / subpages) * subpages + 1;
    const blockEnd = Math.min(blockStart + subpages - 1, lastPage);
    const missing = [];
    for (let page = blockStart; page <= blockEnd; page++) {
      if (!this.#hasPage(config.grid, page)) missing.push(page);
    }
    if (missing.length === 0) return;

    this.#failed = false;
    this.#watchForNextChunk(config, missing, runId);
  }

  /**
   * Loads one chunk at a time: the next chunk is requested when the shopper scrolls near the end of the cards that are
   * already on screen. A safety timer loads whatever is left if nobody scrolls.
   * @param {{ grid: HTMLUListElement }} config
   * @param {number[]} pages
   * @param {number} runId
   */
  #watchForNextChunk(config, pages, runId) {
    this.#queue = [...pages];

    // Marker right after the grid. When it is close to the viewport the shopper reached the end of the loaded cards.
    const sentinel = document.createElement('div');
    sentinel.dataset.phasedSentinel = '';
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'height:1px;width:100%;';
    config.grid.insertAdjacentElement('afterend', sentinel);

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) void this.#loadNext(config, runId);
      },
      { rootMargin: `0px 0px ${NEXT_CHUNK_MARGIN} 0px` }
    );
    this.#observer.observe(sentinel);

    // Skeleton cards only when the shopper is already close to the end and the next chunk is still missing.
    this.#skeletonObserver = new IntersectionObserver(
      (entries) => {
        if (
          entries.some((entry) => entry.isIntersecting) &&
          this.#queue.length > 0 &&
          runId === this.#runId &&
          !this.#failed
        ) {
          this.#showSkeleton(config, [this.#queue[0]]);
        }
      },
      { rootMargin: `0px 0px ${SKELETON_MARGIN} 0px` }
    );
    this.#skeletonObserver.observe(sentinel);

    // Crawlers cannot scroll, so they get the rest of the block quickly; visitors only load more by scrolling (long safety timer).
    const delay = CRAWLER_USER_AGENT.test(navigator.userAgent || '') ? CRAWLER_DELAY_MS : SAFETY_TIMER_MS;
    this.#safetyTimer = window.setTimeout(() => void this.#loadAllRemaining(config, runId), delay);
  }

  /** Loads the next missing chunk (never two at once). */
  async #loadNext(config, runId) {
    if (this.#chunkLoading || this.#failed || runId !== this.#runId || this.#queue.length === 0) return;

    this.#chunkLoading = true;
    const page = this.#queue[0];
    try {
      await this.#loadChunk(config, page, runId);
      if (runId !== this.#runId) return;
      this.#queue.shift();
    } catch (error) {
      console.error('[phased] chunk failed', page, error);
      if (runId === this.#runId) this.#failChunks(config, runId);
      return;
    } finally {
      this.#chunkLoading = false;
    }

    if (this.#queue.length === 0) {
      this.#stopLoading();
      this.#removeSkeleton();
      return;
    }

    // The marker moved down with the new cards: observe it again so the "close to the viewport" check runs again.
    const sentinel = this.querySelector('[data-phased-sentinel]');
    if (sentinel) {
      for (const observer of [this.#observer, this.#skeletonObserver]) {
        observer?.unobserve(sentinel);
        observer?.observe(sentinel);
      }
    }
  }

  /** Safety timer: load everything that is still missing, one chunk after the other. */
  async #loadAllRemaining(config, runId) {
    while (this.#queue.length > 0 && runId === this.#runId && !this.#failed) {
      if (this.#chunkLoading) {
        await new Promise((resolve) => setTimeout(resolve, 250));
        continue;
      }
      await this.#loadNext(config, runId);
    }
  }

  /** A chunk could not be loaded (after the section renderer's retries): stop and offer a button. */
  #failChunks(config, runId) {
    this.#failed = true;
    const pages = [...this.#queue];
    this.#stopLoading();
    this.#removeSkeleton();
    this.#showRetry(config, pages, runId);
  }

  /** Stops the scroll watcher and the safety timer. */
  #stopLoading() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#skeletonObserver?.disconnect();
    this.#skeletonObserver = null;
    window.clearTimeout(this.#safetyTimer);
    this.#safetyTimer = 0;
    this.#queue = [];
    this.querySelectorAll('[data-phased-sentinel]').forEach((el) => el.remove());
  }

  /** @param {number} page */
  #pageUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.hash = '';
    return url;
  }

  /** @param {HTMLUListElement} grid @param {number} page */
  #hasPage(grid, page) {
    return grid.querySelector(`li.product-grid__item[data-page="${page}"]`) !== null;
  }

  /**
   * Fetches one chunk and inserts its cards. Rejects when the fetch (after the section renderer's own retries) or
   * the parsing fails, so the caller can offer a retry for exactly the missing chunks.
   * @param {{ grid: HTMLUListElement }} config
   * @param {number} page
   * @param {number} runId
   */
  async #loadChunk(config, page, runId) {
    const html = await sectionRenderer.getSectionHTML(this.sectionId, false, this.#pageUrl(page));
    if (runId !== this.#runId) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const section = doc.getElementById(`shopify-section-${this.sectionId.replace(/^shopify-section-/, '')}`);
    const items = section ? [...section.querySelectorAll('ul[data-phased="true"] > li.product-grid__item')] : [];
    if (items.length === 0) throw new Error(`[phased] chunk ${page} returned no products`);

    // The server marks the first cards of EVERY response as eager / high priority (it cannot know the chunk is below the
    // fold). Only the first chunk on the page should load images with priority; all later chunks are lazy.
    for (const li of items) {
      li.querySelectorAll('img').forEach((img) => {
        img.setAttribute('loading', 'lazy');
        img.removeAttribute('fetchpriority');
      });
    }

    this.#insertItems(config.grid, page, items);
    this.#removeSkeletonTrail();
  }

  /**
   * Inserts the cards of Shopify page `page` before the first card of a later page (keeps product order).
   * @param {HTMLUListElement} grid
   * @param {number} page
   * @param {Element[]} items
   */
  #insertItems(grid, page, items) {
    const existingIds = new Set([...grid.querySelectorAll('li.product-grid__item')].map((li) => li.id));
    const fresh = items.filter((li) => !li.id || !existingIds.has(li.id)).map((li) => document.importNode(li, true));
    if (fresh.length === 0) return;

    const firstLater = [...grid.querySelectorAll('li.product-grid__item')].find(
      (li) => Number(li.getAttribute('data-page')) > page
    );
    if (firstLater) {
      for (const li of fresh) firstLater.before(li);
    } else {
      grid.append(...fresh);
    }

    // Same re-binding the section renderer does after a morph.
    window.initProductCards?.();
    window.initProductRatings?.();
  }

  /* ------------------------------------------------------------------ */
  /* Skeleton + retry                                                    */
  /* ------------------------------------------------------------------ */

  /**
   * @param {{ grid: HTMLUListElement, chunkSize: number, total: number }} config
   * @param {number[]} pages
   */
  #showSkeleton(config, pages) {
    if (this.#skeletonTrail) return;
    const { grid, chunkSize, total } = config;
    const trail = document.createElement('div');
    trail.className = 'product-grid-skeleton-trail';
    trail.dataset.phasedSkeleton = '';
    trail.setAttribute('aria-hidden', 'true');
    for (const c of grid.classList) {
      if (c.startsWith('product-grid--') || c === 'product-grid-mobile--large') trail.classList.add(c);
    }
    const mobileColumns = grid.style.getPropertyValue('--mobile-columns');
    if (mobileColumns) trail.style.setProperty('--mobile-columns', mobileColumns);

    for (const page of pages) {
      const expected = total > 0 ? Math.min(chunkSize, Math.max(0, total - (page - 1) * chunkSize)) : chunkSize;
      for (let i = 0; i < expected; i++) trail.appendChild(this.#createSkeletonCard());
    }

    // After the marker, so the marker stays at the end of the loaded cards.
    (this.querySelector('[data-phased-sentinel]') ?? grid).insertAdjacentElement('afterend', trail);
    this.#skeletonTrail = trail;
  }

  #createSkeletonCard() {
    const root = document.createElement('div');
    root.className = 'product-grid-skeleton-card';
    root.setAttribute('aria-hidden', 'true');
    root.innerHTML =
      '<div class="product-grid-skeleton-card__media"></div>' +
      '<div class="product-grid-skeleton-card__lines">' +
      '<span class="product-grid-skeleton-card__line product-grid-skeleton-card__line--title"></span>' +
      '<span class="product-grid-skeleton-card__line product-grid-skeleton-card__line--short"></span>' +
      '</div>';
    return root;
  }

  /** Removes the skeleton cards (a chunk arrived). */
  #removeSkeletonTrail() {
    this.#skeletonTrail?.remove();
    this.#skeletonTrail = null;
  }

  #removeSkeleton() {
    this.#skeletonTrail?.remove();
    this.#skeletonTrail = null;
    this.querySelectorAll('[data-phased-skeleton], [data-phased-retry]').forEach((el) => el.remove());
  }

  /**
   * Shows a button when some chunks could not be loaded, so the shopper is never left with a half grid.
   * @param {{ grid: HTMLUListElement }} config
   * @param {number[]} pages
   * @param {number} runId
   */
  #showRetry(config, pages, runId) {
    const wrapper = document.createElement('div');
    wrapper.dataset.phasedRetry = '';
    wrapper.style.cssText = 'display:flex;justify-content:center;padding:24px 0;';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button button-secondary';
    button.textContent = 'Load remaining products';
    button.addEventListener('click', async () => {
      button.disabled = true;
      button.textContent = 'Loading...';
      const results = await Promise.allSettled(pages.map((page) => this.#loadChunk(config, page, runId)));
      if (runId !== this.#runId) return;
      if (results.every((r) => r.status === 'fulfilled')) {
        wrapper.remove();
      } else {
        button.disabled = false;
        button.textContent = 'Load remaining products';
      }
    });

    wrapper.appendChild(button);
    config.grid.insertAdjacentElement('afterend', wrapper);
  }

  /* ------------------------------------------------------------------ */
  /* Pagination (logical pages)                                          */
  /* ------------------------------------------------------------------ */

  /**
   * Pagination control click. `data.page` is a LOGICAL page; the section is rendered from its first chunk.
   * @param {Object<string, string>} data
   * @param {Event} event
   */
  async onPaginationControlClick(data, event) {
    event.preventDefault();

    const config = this.#readConfig();
    const logical = parseInt(data?.page ?? '', 10);
    if (!config || !Number.isFinite(logical) || logical < 1) return;

    const logicalTotal = Math.max(1, Math.ceil(config.lastPage / config.subpages));
    const target = Math.min(logical, logicalTotal);
    const startPage = (target - 1) * config.subpages + 1;

    const loaderElement = showLoader();
    try {
      this.#runId++;
      this.#stopLoading();
      this.#removeSkeleton();

      // Keep the browser URL on the logical page (shareable); the section is rendered from the first chunk.
      const browserUrl = new URL(window.location.href);
      browserUrl.searchParams.set('page', String(target));
      window.history.replaceState({}, '', browserUrl.toString());

      await sectionRenderer.renderSection(this.sectionId, { cache: false, url: this.#pageUrl(startPage) });

      requestAnimationFrame(() => requestAnimationFrame(() => this.#scrollToGrid()));
    } catch (error) {
      console.error('[phased] pagination failed', error);
    } finally {
      hideLoader(loaderElement);
    }
  }

  #scrollToGrid() {
    const target = this.querySelector('#ResultsList') ?? this;
    const top = target.getBoundingClientRect().top + window.scrollY - this.#getHeaderHeight();
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  #getHeaderHeight() {
    if (window.innerWidth < 750) {
      const headerSection = document.querySelector('.header-section');
      if (headerSection instanceof HTMLElement) return headerSection.offsetHeight;
    } else {
      const parsed = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')?.trim());
      if (!isNaN(parsed) && parsed > 0) return parsed;
    }
    const header = document.querySelector('header-component');
    return header instanceof HTMLElement ? header.offsetHeight : 0;
  }

  /* ------------------------------------------------------------------ */
  /* Grid layout toggle (same behaviour as results-list.js)              */
  /* ------------------------------------------------------------------ */

  /** @param {Event} event */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;
    this.#animateLayoutChange(target.value);
  }

  /** @param {string} value */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;
    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /** @param {string} value */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /** @param {MediaQueryListEvent} event */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsListPhased);
}
